import React, { useEffect, useState } from "react";
import { Outlet, useLocation, Link, NavLink } from "react-router-dom";
import { ArrowRight, ChevronDown, Globe, Linkedin, Menu, MessageCircle, Search, X, Youtube } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { LANG_LABELS } from "@/i18n/translations";
import { useSettings } from "@/context/SettingsContext";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { NexisAssistWidget } from "@/components/NexisAssistWidget";

const NAV = [
  { to: "/", label: { pt: "Início", en: "Home" }, end: true },
  { to: "/solutions", label: { pt: "Soluções & Trade", en: "Solutions & Trade" } },
  { to: "/sourcing", label: { pt: "Sourcing", en: "Sourcing" } },
  { to: "/experts", label: { pt: "Especialistas & Formação", en: "Experts & Training" } },
  { to: "/eco-collection", label: { pt: "Eco Collection", en: "Eco Collection" } },
  { to: "/network", label: { pt: "Network", en: "Network" } },
  { to: "/projects", label: { pt: "Projetos", en: "Projects" } },
  { to: "/about", label: { pt: "Sobre", en: "About" } },
  { to: "/contact", label: { pt: "Contacto", en: "Contact" } },
];

const LANGS_UI = ["pt", "en", "de"];

function Brand({ onClick, light = false }) {
  return (
    <Link to="/" onClick={onClick} data-testid={light ? undefined : "shell-logo"} className="group flex shrink-0 items-center gap-3" aria-label="BSGOTECH — início">
      <span className={`flex h-11 w-11 items-center justify-center rounded-full border ${light ? "border-white/20 bg-white/5" : "border-white/15 bg-white/10"}`}>
        <img src="/logo-mark.png" alt="" width="30" height="30" className="h-[30px] w-[30px] object-contain transition-transform duration-500 group-hover:scale-105" />
      </span>
      <span className="leading-none">
        <span className={`block font-display text-[1.28rem] font-extrabold tracking-[-0.035em] ${light ? "text-white" : "text-white"}`}>BSGO<span className="text-[#f3bf57]">TECH</span></span>
        <span className="mt-1.5 block text-[7px] font-semibold tracking-[0.2em] text-white/55">CONNECT · SOURCE · BUILD · DELIVER</span>
      </span>
    </Link>
  );
}

function LangMenu({ mobile = false }) {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button type="button" data-testid="shell-lang-switcher" onClick={() => setOpen((value) => !value)} aria-expanded={open}
        className={`flex items-center gap-1.5 rounded-md px-2.5 py-2 text-xs font-semibold transition-colors ${mobile ? "text-slate-700 hover:bg-slate-100" : "text-white/75 hover:bg-white/10 hover:text-white"}`}>
        <Globe size={15} aria-hidden="true" /> {LANG_LABELS[lang]} <ChevronDown size={12} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-28 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-xl" data-testid="shell-lang-menu">
          {LANGS_UI.map((item) => (
            <button type="button" key={item} data-testid={`shell-lang-${item}`} onClick={() => { setLang(item); setOpen(false); }}
              className={`block w-full px-4 py-2 text-left text-sm transition-colors ${lang === item ? "bg-slate-50 font-semibold text-[#0a3158]" : "text-slate-600 hover:bg-slate-50"}`}>
              {LANG_LABELS[item]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Header() {
  const { lc } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-white/10 bg-[#04172a]/95 text-white shadow-[0_10px_30px_rgba(0,0,0,0.12)] backdrop-blur-xl" data-testid="shell-header">
      <div className="mx-auto flex h-[74px] max-w-[1500px] items-center justify-between gap-5 px-5 lg:px-8">
        <Brand />
        <nav className="hidden items-center gap-5 xl:flex" data-testid="shell-nav" aria-label={lc({ pt: "Navegação principal", en: "Main navigation" })}>
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={`nav-${item.to === "/" ? "home" : item.to.replace(/\//g, "")}`}
              className={({ isActive }) => `relative py-7 text-[12px] font-medium transition-colors after:absolute after:inset-x-0 after:bottom-[18px] after:h-px after:origin-left after:bg-[#f3bf57] after:transition-transform ${isActive ? "text-white after:scale-x-100" : "text-white/65 after:scale-x-0 hover:text-white hover:after:scale-x-100"}`}>
              {lc(item.label)}
            </NavLink>
          ))}
        </nav>
        <div className="flex shrink-0 items-center gap-1.5">
          <Link to="/solutions" aria-label={lc({ pt: "Pesquisar soluções", en: "Search solutions" })} className="hidden rounded-md p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white sm:inline-flex"><Search size={17} /></Link>
          <div className="hidden sm:block"><LangMenu /></div>
          <Link to="/nexis-quiz" data-testid="shell-request-cta" className="hidden items-center gap-2 rounded-md bg-[#f3bf57] px-4 py-2.5 text-xs font-bold text-[#07192b] shadow-[0_8px_25px_rgba(243,191,87,0.18)] transition-colors hover:bg-[#ffd477] lg:inline-flex">
            {lc({ pt: "Diga-nos o que precisa", en: "Tell us what you need" })}<ArrowRight size={14} />
          </Link>
          <button type="button" data-testid="shell-menu-toggle" onClick={() => setOpen((value) => !value)} className="rounded-md p-2 text-white transition-colors hover:bg-white/10 xl:hidden" aria-label="Menu" aria-expanded={open} aria-controls="mobile-navigation">
            {open ? <X size={23} /> : <Menu size={23} />}
          </button>
        </div>
      </div>
      {open && (
        <div id="mobile-navigation" className="h-[calc(100vh-74px)] overflow-y-auto border-t border-white/10 bg-white px-5 py-5 text-slate-900 xl:hidden" data-testid="shell-mobile-menu">
          <nav className="mx-auto flex max-w-2xl flex-col" aria-label={lc({ pt: "Navegação móvel", en: "Mobile navigation" })}>
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} data-testid={`mobile-nav-${item.to === "/" ? "home" : item.to.replace(/\//g, "")}`}
                className={({ isActive }) => `flex items-center justify-between border-b border-slate-100 py-3.5 text-sm ${isActive ? "font-semibold text-[#0a3158]" : "text-slate-700"}`}>
                {lc(item.label)}<ArrowRight size={14} className="text-slate-300" />
              </NavLink>
            ))}
          </nav>
          <div className="mx-auto mt-5 flex max-w-2xl items-center justify-between gap-3">
            <LangMenu mobile />
            <Link to="/nexis-quiz" data-testid="mobile-request-cta" className="inline-flex items-center gap-2 rounded-md bg-[#f3bf57] px-4 py-3 text-xs font-bold text-[#07192b]">
              {lc({ pt: "Diga-nos o que precisa", en: "Tell us what you need" })}<ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { lc } = useI18n();
  const { settings } = useSettings();
  const year = new Date().getFullYear();
  const wa = settings.whatsapp && !settings.whatsapp.includes("[") ? `https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, "")}` : null;
  const groups = [
    { title: lc({ pt: "Soluções", en: "Solutions" }), links: [["/solutions", "HX Ecosystems"], ["/solutions", "BSGOTECH Trade"], ["/sourcing", "Global Sourcing"], ["/eco-collection", "Eco Collection"]] },
    { title: lc({ pt: "Capacidades", en: "Capabilities" }), links: [["/experts", lc({ pt: "Specialists & Training", en: "Specialists & Training" })], ["/nexis", "NEXIS"], ["/nexis-quiz", "NEXIS Assist"], ["/bs-call-pro", "BS Call Pro"], ["/nexis-builder", "NEXIS Builder"]] },
    { title: "BSGOTECH", links: [["/about", lc({ pt: "Sobre", en: "About" })], ["/contact", lc({ pt: "Contacto", en: "Contact" })], ["/faq", "FAQ"], ["/privacy", lc({ pt: "Privacidade", en: "Privacy" })]] },
  ];
  return (
    <footer className="bg-[#041526] text-white" data-testid="shell-footer">
      <div className="mx-auto grid max-w-[1400px] gap-12 px-6 py-14 md:grid-cols-[1.25fr_2fr] lg:px-8 lg:py-16">
        <div className="max-w-sm">
          <Brand light />
          <p className="mt-6 text-sm leading-6 text-white/55">{lc({ pt: "Coordenamos soluções, fornecedores e capacidades para pessoas, negócios e organizações.", en: "We coordinate solutions, suppliers and capabilities for people, businesses and organisations." })}</p>
          <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#f3bf57]">São Tomé e Príncipe · África · Mundo</p>
        </div>
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
          {groups.map((group) => <div key={group.title}><h2 className="text-xs font-bold uppercase tracking-[0.16em] text-white">{group.title}</h2><div className="mt-4 flex flex-col gap-3">{group.links.map(([to, label]) => <Link key={to} to={to} className="text-sm text-white/55 transition-colors hover:text-[#f3bf57]">{label}</Link>)}</div></div>)}
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-4 px-6 py-5 text-xs text-white/40 sm:flex-row sm:items-center sm:justify-between lg:px-8">
          <span>© {year} BSGOTECH, Lda. · {lc({ pt: "São Tomé e Príncipe", en: "São Tomé and Príncipe" })}</span>
          <div className="flex items-center gap-3">
            <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="rounded-full p-2 transition-colors hover:bg-white/10 hover:text-white"><Linkedin size={16} /></a>
            <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="rounded-full p-2 transition-colors hover:bg-white/10 hover:text-white"><Youtube size={16} /></a>
            {wa && <a href={wa} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="rounded-full p-2 transition-colors hover:bg-white/10 hover:text-white"><MessageCircle size={16} /></a>}
            <Link to="/admin" data-testid="footer-admin-link" className="ml-2 transition-colors hover:text-white">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [pathname]);
  return null;
}

export function AppShell() {
  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <ScrollToTop />
      <Header />
      <main className="flex-1 pt-[74px]"><Outlet /></main>
      <Footer />
      <WhatsAppButton />
      <NexisAssistWidget />
    </div>
  );
}
